import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { GameStatus } from '@sorare/core/src/__generated__/globalTypes';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';

import type { TournamentGameType } from '@sorare/gameplay/src/components/Bracket';
import {
  Bracket,
  TournamentAlignment,
} from '@sorare/gameplay/src/components/Bracket';

import type {
  getNBAPlayoffsFixtureGames,
  getNBAPlayoffsFixtureGamesVariables,
} from './__generated__/index.graphql.ts';
import { PlayoffsLayout, getNBATeam } from './utils.tsx';

type Game = {
  slot1?: Parameters<typeof getNBATeam>[0] | null;
  slot2?: Parameters<typeof getNBATeam>[0] | null;
  slot1Parent?: Game;
  slot2Parent?: Game;
};

const hydrate = (data: Game, gameResults: GamesResults): TournamentGameType => {
  return {
    slot1: getNBATeam(
      data.slot1,
      data.slot1 && data.slot2
        ? gameResults[data.slot1]?.[data.slot2] ?? 0
        : undefined
    ),
    slot2: getNBATeam(
      data.slot2,
      data.slot1 && data.slot2
        ? gameResults[data.slot2]?.[data.slot1] ?? 0
        : undefined
    ),
    slot1Parent: data.slot1Parent
      ? hydrate(data.slot1Parent, gameResults)
      : undefined,
    slot2Parent: data.slot2Parent
      ? hydrate(data.slot2Parent, gameResults)
      : undefined,
  };
};

const GET_PLAYOFFS_FIXTURE_GAMES = gql`
  query getNBAPlayoffsFixtureGames {
    so5 {
      playoffsGameWeek1: so5Fixture(slug: "nba-19-25-apr-2025") {
        slug
        ...playoffsGames
      }
      playoffsGameWeek2: so5Fixture(slug: "nba-25-apr-5-may-2025") {
        slug
        ...playoffsGames
      }
      playoffsGameWeek3: so5Fixture(slug: "nba-5-20-may-2025") {
        slug
        ...playoffsGames
      }
      playoffsGameWeek4: so5Fixture(slug: "nba-22-may-3-jun-2025") {
        slug
        ...playoffsGames
      }
      playoffsGameWeek5: so5Fixture(slug: "nba-6-23-jun-2025") {
        slug
        ...playoffsGames
      }
      # playoffsGameWeek6: so5Fixture(slug: "nba-2023-gameweek-54") {
      #   slug
      #   ...playoffsGames
      # }
      # playoffsGameWeek7: so5Fixture(slug: "nba-2023-gameweek-55") {
      #   slug
      #   ...playoffsGames
      # }
      # playoffsGameWeek8: so5Fixture(slug: "nba-2023-gameweek-56") {
      #   slug
      #   ...playoffsGames
      # }
    }
  }
  fragment playoffsGames on So5Fixture {
    slug
    anyGames {
      id
      awayScore
      homeScore
      statusTyped
      awayTeam {
        slug
        shortName
      }
      homeTeam {
        slug
        shortName
      }
    }
  }
` as TypedDocumentNode<
  getNBAPlayoffsFixtureGames,
  getNBAPlayoffsFixtureGamesVariables
>;

type GamesResults = Record<string, Record<string, number>>;

const usePlayoffsGamesResults = (): GamesResults => {
  const { data } = useQuery(GET_PLAYOFFS_FIXTURE_GAMES, {});

  return [
    ...(data?.so5.playoffsGameWeek1?.anyGames ?? []),
    ...(data?.so5.playoffsGameWeek2?.anyGames ?? []),
    ...(data?.so5.playoffsGameWeek3?.anyGames ?? []),
    ...(data?.so5.playoffsGameWeek4?.anyGames ?? []),
    ...(data?.so5.playoffsGameWeek5?.anyGames ?? []),
    // ...(data?.so5.playoffsGameWeek6?.anyGames ?? []),
    // ...(data?.so5.playoffsGameWeek7?.anyGames ?? []),
    // ...(data?.so5.playoffsGameWeek8?.anyGames ?? []),
  ]
    .filter(g => g.statusTyped === GameStatus.played)
    .reduce<GamesResults>((acc, g) => {
      const [winner, loser] =
        g.awayScore > g.homeScore
          ? [g.awayTeam?.shortName, g.homeTeam?.shortName]
          : [g.homeTeam?.shortName, g.awayTeam?.shortName];
      if (!winner || !loser) {
        return acc;
      }
      if (!(winner in acc)) {
        acc[winner] = {};
      }
      if (!(loser in acc[winner])) {
        acc[winner][loser] = 0;
      }
      acc[winner][loser] += 1;
      return acc;
    }, {});
};

export const Playoffs = () => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const {
    flags: { nbaPlayoffsConfig = {} },
  } = useFeatureFlags();

  const games = usePlayoffsGamesResults();

  const GAME = useMemo(() => {
    return nbaPlayoffsConfig && hydrate(nbaPlayoffsConfig, games);
  }, [nbaPlayoffsConfig, games]);

  if (Object.keys(nbaPlayoffsConfig).length === 0) {
    return null;
  }
  return (
    <PlayoffsLayout>
      <Bracket
        winner={getNBATeam(nbaPlayoffsConfig.winner)}
        game={GAME}
        alignment={
          isTabletAndAbove
            ? TournamentAlignment.CENTER
            : TournamentAlignment.LEFT
        }
      />
    </PlayoffsLayout>
  );
};
